import { defineStore } from 'pinia';
import { ref } from 'vue';

import { api } from '~/lib/api';
import { onRealtime } from '~/lib/realtime';
import type { NotificationRow } from '~/stores/notifications';

/**
 * Tracks how many "new" (status=created) orders the active store has — the
 * count badge shown next to Siparişler in the sidebar and the bottom tab bar.
 *
 * The count is store-scoped: `refresh()` reads whatever store id the api layer
 * currently sends as X-Store-Id, so callers must re-fetch after switching
 * stores. Realtime `order.new` events bump it live.
 */
export const useOrderBadgeStore = defineStore('order-badge', () => {
  const newCount = ref(0);
  let unsubscribe: (() => void) | null = null;
  // Collapse bursts of realtime events into a single re-fetch: a bulk sync can
  // emit hundreds of `order.new` in under a second, and one refetch each would
  // flood the API and trip the rate limiter.
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  const REFRESH_DEBOUNCE_MS = 600;

  function scheduleRefresh(): void {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refresh();
    }, REFRESH_DEBOUNCE_MS);
  }

  /** Re-read the new-order count for the active store. Resilient: never throws. */
  async function refresh(): Promise<void> {
    try {
      // pageSize=1 — we only need `total`, not the rows themselves.
      const res = await api.get<{ total: number }>('/orders', {
        params: { status: 'created', page: 1, pageSize: 1 },
      });
      newCount.value = res.data.total;
    } catch {
      // No active store / transient failure — drop the badge rather than show
      // a stale number.
      newCount.value = 0;
    }
  }

  /**
   * Subscribe to the realtime stream so an incoming order bumps the badge
   * without a navigation. Idempotent — safe to call on every login.
   */
  function attach(): void {
    if (unsubscribe) return;
    unsubscribe = onRealtime((e) => {
      if (e.type !== 'notification:new') return;
      const row = e.payload as NotificationRow;
      // Re-fetch instead of a naive +1 so the count stays correct for the
      // active store even when the notification belongs to a different one.
      if (row.type === 'order.new') scheduleRefresh();
    });
  }

  function reset(): void {
    newCount.value = 0;
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  }

  return { newCount, refresh, attach, reset };
});
